import React, { useState, useMemo } from "react";
import { ResponsiveSankey } from "@nivo/sankey";
import { Box, Typography } from "@mui/material";
import "../../../style/pages-style/buildings/ResultTab.css";

const COLORS = {
    Embodied: "#ef4444", Operational: "#f59e0b", Biogenic: "#10b981", Product: "#3b82f6", Gray: "#64748b"
};

const SECTION_COLORS = {
    "Substructure": "#0ea5e9", "substructure": "#0ea5e9",
    "Interiors": "#ec4899", "interiors": "#ec4899", // Deep Pink
    "Shell": "#14b8a6", "shell": "#14b8a6",
    "Services": "#f97316", "services": "#f97316",
    "Other": "#94a3b8",
    "Operational Carbon": "#f59e0b", "operational_carbon": "#f59e0b"
};

const formatNumber = (num) => {
    if (!num || num === 0) return '0';
    const absNum = Math.abs(num);
    if (absNum >= 1000000) {
        return (num / 1000000).toFixed(1) + 'M';
    } else if (absNum >= 1000) {
        return (num / 1000).toFixed(1) + 'k';
    }
    return Math.round(num).toString();
};

const SankeyBreakdown = ({ data }) => {
    const [flowMode, setFlowMode] = useState("stages"); // "stages" or "materials"

    // Refined Professional Colors
    const STAGE_COLORS = {
        "A1-A3": "#10b981", // Emerald
        "A4-A5": "#3b82f6", // Blue
        "B": "#f59e0b",     // Amber
        "B6-B7": "#fbbf24", // Yellow
        "C": "#ef4444",     // Red
        "D": "#8b5cf6"      // Violet
    };

    const sankeyData = useMemo(() => {
        if (!data || !data.flowData) return { nodes: [], links: [] };

        let nodes = new Set();
        let links = [];

        if (flowMode === 'stages') {
            // Stage -> Section
            data.flowData.stageToSection?.forEach(d => {
                const stageGroup = d.stage_group || "Other";
                const section = d.section || "Unassigned";
                nodes.add(stageGroup);
                nodes.add(section);
                links.push({ source: stageGroup, target: section, value: d.impact });
            });
            // Section -> Sub
            data.buildingSections?.forEach(sec => {
                const category = sec.category || "Unassigned";
                sec.subs?.forEach(sub => {
                    const subName = sub.name || "Unassigned";
                    nodes.add(category);
                    nodes.add(subName);
                    links.push({ source: category, target: subName, value: sub.impact });
                });
            });
        } else {
            // Section -> Sub
            data.buildingSections?.forEach(sec => {
                const category = sec.category || "Unassigned";
                sec.subs?.forEach(sub => {
                    const subName = sub.name || "Unassigned";
                    nodes.add(category);
                    nodes.add(subName);
                    links.push({ source: category, target: subName, value: sub.impact });
                });
            });
            // Sub -> Material
            data.flowData.subToMaterial?.forEach(d => {
                const sub = d.sub || "Unassigned";
                const material = d.material || "Unknown Material";
                nodes.add(sub);
                nodes.add(material);
                links.push({ source: sub, target: material, value: d.impact });
            });
        }

        // Filter out zero/negative values, null sources/targets, and self loops
        links = links.filter(l => Boolean(l.source) && Boolean(l.target) && l.source !== l.target && Number(l.value) > 0);
        const activeNodes = new Set();
        links.forEach(l => { 
            if (l.source) activeNodes.add(l.source); 
            if (l.target) activeNodes.add(l.target); 
        });

        return {
            nodes: Array.from(activeNodes).filter(Boolean).map(id => {
                let label = String(id);
                if (id === 'whole_building') label = 'Operational Carbon';

                return {
                    id,
                    label: label.charAt(0).toUpperCase() + label.slice(1),
                    nodeColor: STAGE_COLORS[id] || SECTION_COLORS[id] || COLORS.Gray
                };
            }),
            links: links
        };
    }, [data, flowMode]);

    return (
        <Box
            className="sankey-breakdown-container"
            sx={{
                width: "100%",
                overflow: "hidden",
                background: '#fff',
                borderRadius: '16px',
                p: 0
            }}
        >
            {/* Centered Flow Selector */}
            <Box sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: '12px',
                padding: '24px 0',
                background: 'linear-gradient(to bottom, #f8fafc, #ffffff)',
                borderBottom: '1px solid #f1f5f9',
                mb: 3
            }}>
                <Typography sx={{
                    fontSize: '11px',
                    fontWeight: 800,
                    color: '#94a3b8',
                    textTransform: 'uppercase',
                    letterSpacing: '1.5px'
                }}>
                    Flow Visualization Mode
                </Typography>
                <select
                    value={flowMode}
                    onChange={(e) => setFlowMode(e.target.value)}
                    style={{
                        padding: '12px 24px',
                        borderRadius: '100px', // Pill shape
                        border: '2px solid #e2e8f0',
                        fontSize: '14px',
                        fontWeight: 700,
                        color: '#1e293b',
                        cursor: 'pointer',
                        outline: 'none',
                        background: '#fff',
                        transition: 'all 0.2s ease',
                        minWidth: '360px',
                        textAlign: 'center',
                        boxShadow: '0 4px 6px -1px rgba(0,0,0,0.05)'
                    }}
                >
                    <option value="stages">LCA Phases → Building Sections</option>
                    <option value="materials">Sections → Subcategories → Materials</option>
                </select>
            </Box>

            <Box sx={{ width: '100%', height: 620, overflowX: 'auto', px: 3, pb: 4 }}>
                <Box sx={{ height: 550, width: "100%", minWidth: 900 }}>
                    {sankeyData.nodes.length >= 2 && sankeyData.links.length > 0 ? (
                        <ResponsiveSankey
                            data={sankeyData}
                            margin={{ top: 20, right: 60, bottom: 20, left: 60 }}
                            align="justify"
                            colors={node => node.nodeColor || '#cbd5e1'}
                            nodeOpacity={1}
                            nodeThickness={24}
                            nodeInnerPadding={2}
                            nodeSpacing={24}
                            nodeBorderWidth={0}
                            linkOpacity={0.4}
                            linkBlendMode="multiply"
                            enableLinkGradient={true}
                            labelPosition="inside"
                            labelOrientation="horizontal"
                            labelPadding={16}
                            labelTextColor="#1e293b"
                            theme={{
                                labels: {
                                    text: {
                                        fontSize: 13,
                                        fill: "#334155",
                                        fontFamily: "Poppins, sans-serif",
                                        fontWeight: 600,
                                        textShadow: '0 1px 2px rgba(255,255,255,0.5)'
                                    },
                                },
                                tooltip: {
                                    container: {
                                        background: '#1e293b',
                                        color: '#fff',
                                        fontSize: '12px',
                                        borderRadius: '8px',
                                        boxShadow: '0 10px 15px -3px rgba(0,0,0,0.2)',
                                        padding: '8px 12px',
                                        border: 'none'
                                    }
                                }
                            }}
                            label="label"
                            valueFormat={(value) => `${Math.round(value).toLocaleString()} kgCO₂e`}
                        />
                    ) : (
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', color: '#94a3b8', fontStyle: 'italic' }}>
                            Analyzing flow data...
                        </Box>
                    )}
                </Box>
            </Box>
        </Box>
    );
};

export default SankeyBreakdown;
